import React, { Component } from 'react';
import { connect } from 'react-redux';
import withRouter from 'with-router';
import classNames from 'classnames';
import { alpha, styled } from '@mui/material/styles';
import { Typography, TextField, List, ListItemButton, ListItemAvatar, ListItemText, CircularProgress, Alert, InputAdornment, Accordion, AccordionSummary, AccordionDetails, Divider, Collapse } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DropzoneArea } from './MUI5Dropzone';
import API from '../api/api';
import { isShopImageRef, shopImageFilename, shopImageRef, resolveShopImage, rememberShopImage, shopImageGid, isMediaImageGid, resolveMediaImage, rememberMediaImage } from '../utils/shopImages';
import LoadingIndicator from '../components/LoadingIndicator';
import { probeImage } from '../utils/imageProbe';
import { Trans, withTranslation } from 'react-i18next';

const PREFIX = 'ImageField';
const classes = {
  box: `${PREFIX}-box`,
  labelChip: `${PREFIX}-labelChip`,
  thumb: `${PREFIX}-thumb`,
  previewImage: `${PREFIX}-previewImage`,
  largeImage: `${PREFIX}-largeImage`,
  editBtn: `${PREFIX}-editBtn`,
  boxClickable: `${PREFIX}-boxClickable`,
  filename: `${PREFIX}-filename`,
  changeBtn: `${PREFIX}-changeBtn`,
  image: `${PREFIX}-image`,
  clearBtn: `${PREFIX}-clearBtn`,
  closeBtn: `${PREFIX}-closeBtn`,
  dropzoneWrapper: `${PREFIX}-dropzoneWrapper`,
  dropzone: `${PREFIX}-dropzone`,
  dropzoneText: `${PREFIX}-dropzoneText`,
  dialogContent: `${PREFIX}-dialogContent`,
  compareImage: `${PREFIX}-compareImage`,
  compareStage: `${PREFIX}-compareStage`,
  broken: `${PREFIX}-broken`,
  highlighted: `${PREFIX}-highlighted`,
};

// One instance renders one side of the field row (variant "source" | "target").
// Layout is a vertical stack on purpose: the image alt tag becomes a second
// translatable line here later.
const Root = styled('div')(({ theme }) => ({
  [`&.${classes.box}`]: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    // height, padding and thumb size are set per row in render (see boxStyle); both variants
    // and both target states land on the same height
    minHeight: 90,
    padding: 20,
    // the outline of the text fields next to it, not the lighter divider
    border: `solid 1px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
  },
  // the source side sits on the recessed surface like the read-only text fields next to it
  [`&.ly-image-field-source`]: {
    backgroundColor: theme.palette.surfaceRecessed,
    [`& .${classes.labelChip}`]: {
      backgroundColor: theme.palette.surfaceRecessed,
    },
  },
  [`& .${classes.labelChip}`]: {
    position: 'absolute',
    zIndex: 1,
    left: 10,
    top: -8,
    display: 'inline-block',
    padding: '0 4px',
    borderRadius: 4,
    fontSize: 12,
    lineHeight: '16px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  [`& .${classes.thumb}`]: {
    width: 'var(--ly-image-thumb, 50px)',
    height: 'var(--ly-image-thumb, 50px)',
    flex: '0 0 var(--ly-image-thumb, 50px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `solid 1px ${theme.palette.divider}`,
    backgroundColor: theme.palette.surfaceRecessed,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    cursor: 'pointer',
  },
  // the large view: the image fills a fixed-height stage, so both sides line up whatever the
  // aspect ratios are; a click on the stage does what the small thumb does
  [`& .${classes.largeImage}`]: {
    // the large view's image stage; the image scales into it, so this is the image size
    height: 540,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: 6,
    border: `solid 1px ${theme.palette.divider}`,
    backgroundColor: theme.palette.surfaceRecessed,
    cursor: 'pointer',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
  },
  [`& .${classes.previewImage}`]: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '80vh',
    margin: '0 auto',
    borderRadius: 4,
  },
  [`& .${classes.filename}`]: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  [`& .${classes.changeBtn}`]: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  [`& .${classes.editBtn}`]: {
    flexShrink: 0,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  // listed in the broken-images banner: the outdated fields' warning outline
  [`&.${classes.broken}`]: {
    outline: `2px solid ${theme.palette.warning.main}`,
    outlineOffset: -1,
  },
  // the field a deeplink points at, outlined like a focused text field
  [`&.${classes.highlighted}`]: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -1,
  },
  [`&.${classes.boxClickable}`]: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  [`& .${classes.image}`]: {
    width: '100%',
    border: `solid 1px ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  [`& .${classes.clearBtn}`]: {
    position: 'absolute',
    top: 12,
    right: 34,
    backgroundColor: theme.palette.error.dark,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    }
  },
  [`& .${classes.dropzoneWrapper}`]: {
    width: '100%',
  },
  [`& .${classes.dropzone}`]: {
    '& svg': {
      display: 'none'
    },
    width: '100%',
    padding: 16,
    margin: 0,
    border: `dashed 2px ${theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.accentMuted}`,
    background: theme.palette.background.paper,
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  [`& .${classes.dropzoneText}`]: {
    fontSize: '15px',
    lineHeight: '1.5',
    color: theme.palette.text.disabled,
    fontStyle: 'italic'
  },
  [`& .${classes.dialogContent}`]: {
    padding: '0 24px 24px 24px',
    position: 'relative',
    overflow: 'visible',
  },
}));

// The dialog portals out of Root, so it needs its own style scope; also keeps the
// chooser compact enough for short screens (the dropzone's own defaults are ~170px tall).
// one edge length for the two square compare boxes, the toggle handle and the options column
const COMPARE_BOX_SIZE = 'clamp(200px, 20vw, 355px)';
// title, one compare row with details, options column, padding - the height the edit dialog
// usually has on a laptop; its top offset is chosen to centre this
const EDIT_DIALOG_USUAL_HEIGHT = 560;

// how the last image dialog on the page was left (picker option, original comparison) - the
// next one opens the same way, whichever row it belongs to. The comparison starts open: when
// stepping through images the original is what tells them apart
let lastDialogView = { expandedOption: null, showOriginal: true };

// what is known about an image URL, shared by every image field on the page: a dialog opened
// a second time (or the next row's dialog showing the same file) neither measures nor HEADs
// again, and shows the image without a spinner - the browser serves it from its cache
const imageMeta = {
  dimensions: {}, // url -> {width, height}
  fileSizes: {},  // url -> bytes (null: unknown, e.g. a host without CORS headers)
  loaded: {},     // url -> true once an <img> for it has fired load
};
// the library's first page (no query) as the last dialog fetched it; every row's dialog
// starts from it instead of asking again. Dropped after an upload, which adds a file
let initialFilePage = null;

const ChooserArea = styled('div')(({ theme }) => ({
  // the FRAME is the constant, not the image: two identical outlined boxes (text-field
  // border color) with a recessed letterbox, so differing aspect ratios stay tidy
  // the compare box: the image stage on top, the details as the box's footer
  [`& .${classes.compareImage}`]: {
    width: '100%',
    aspectRatio: '1 / 1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: 8,
    boxSizing: 'border-box',
    border: `solid 1px ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.surfaceRecessed,
  },
  [`& .${classes.compareStage}`]: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [`& .${classes.dropzone}`]: {
    // generous drop target - the two-column dialog gives it the room
    minHeight: '140px !important',
    padding: '20px !important',
    '& > svg': {
      fontSize: '38px !important',
      marginBottom: '4px !important',
    },
    '& .MuiTypography-root': {
      fontSize: 14,
    },
  },
}));

// Image translation is intentionally available on all plans - never plan-gate this field.
class ImageField extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isPending: false,
      showEditDialog: false,
      // originals to show where a sized rendition failed to load (url => true)
      sizedFailed: {},
      showPreview: null,
      brokenUrls: {},
      selectedFile: null,
      selectedFilePreview: null,
      uploadError: null,
      showOriginal: false,
      urlInput: '',
      expandedOption: null,
      selectedFileUrl: null,
      fileQuery: '',
      fileResults: null,
      fileCursor: null,
      fileSearchPending: false,
      // shopify://shop_images references -> CDN URL (undefined: resolving, null: no such file)
      resolvedRefs: {},
      // typed URLs -> did the image load (undefined: probing, true/false)
      urlChecks: {},
      // bumped whenever imageMeta learns something, so the details and warning re-render
      metaTick: 0,
    }
  }

  componentDidMount() {
    this.resolveRefs();
    // opening an item checks again: a file deleted since the last check still reads as loading
    this.probeValues(true);
    if(this.props.autoOpen) this.openFromNavigation();
  }

  componentDidUpdate(prevProps, prevState) {
    if(prevProps.source !== this.props.source || prevProps.target !== this.props.target) this.resolveRefs();
    if(prevProps.source !== this.props.source || prevProps.target !== this.props.target || prevState.resolvedRefs !== this.state.resolvedRefs) this.probeValues();
    if(this.props.autoOpen && !prevProps.autoOpen) this.openFromNavigation();
    if(prevState.showEditDialog && !this.state.showEditDialog) {
      lastDialogView = { expandedOption: this.state.expandedOption, showOriginal: this.state.showOriginal };
    }
  }

  // the neighbor's dialog handed over to this row (see TranslationFields.handleImageNavigate)
  openFromNavigation = () => {
    this.handleOpenEditDialog();
    // what the merchant had open stays open on the next image: the picker option and the
    // original comparison
    // the handover carries the view explicitly: the closing dialog's commit may run after this one
    const view = this.props.autoOpenView || lastDialogView;
    this.setState({ expandedOption: view.expandedOption || null, showOriginal: view.showOriginal === true });
    if(this.props.onAutoOpened) this.props.onAutoOpened();
  }

  navigate = (direction) => {
    this.setState({ showEditDialog: false });
    this.props.navigation.onNavigate(direction, { expandedOption: this.state.expandedOption, showOriginal: this.state.showOriginal });
  }

  // a value that names a file rather than a URL: a theme's shop image reference or a file
  // reference metafield's media gid. Both resolve to a CDN URL for display
  isReference = (value) => isShopImageRef(value) || isMediaImageGid(value);

  resolveRefs = () => {
    [this.props.source, this.props.target].forEach(value => {
      if(!this.isReference(value) || this.state.resolvedRefs[value] !== undefined) return;
      const resolve = isShopImageRef(value) ? resolveShopImage : resolveMediaImage;
      resolve(value.trim(), url => {
        if(this.unmounted) return;
        this.setState(prev => ({ resolvedRefs: { ...prev.resolvedRefs, [value]: url } }));
      });
    });
  }

  // what the browser can load for a field value: the value itself, or the resolved reference
  displayUrl = (value) => this.isReference(value) ? this.state.resolvedRefs[value] : value;

  // Shopify's CDN scales on request: the rows show a rendition sized for their box instead of
  // the original (a product photo of several MB, times two sides, times every image of the
  // product). The size goes into the file name ("name_120x.jpg") - the ?width= parameter is not
  // honoured on the /s/files/ URLs Shopify hands out, the suffix is, and a legacy suffix already
  // in the name (_large, _600x600) is replaced, a doubled one 404s. Other hosts get the original.
  // The zoom preview and the dialog keep the original, the dialog measures the real dimensions
  // from it. A rendition that fails falls back to the original (sizedFailed)
  sizedUrl = (url, width) => {
    if(typeof url !== 'string' || !/^https?:\/\//.test(url)) return url;
    if(this.state.sizedFailed[url]) return url;
    let parsed;
    try { parsed = new URL(url); } catch (e) { return url; }
    if(parsed.hostname !== 'cdn.shopify.com' && !/\/cdn\/shop\//.test(parsed.pathname)) return url;
    const match = parsed.pathname.match(/^(.*?)(?:_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d*x\d*))?(\.[a-z0-9]+)$/i);
    if(!match) return url;
    parsed.pathname = `${match[1]}_${width}x${match[2]}`;
    return parsed.toString();
  }

  // the rendition could not be loaded (a name the CDN does not resize): show the original
  handleSizedError = (url) => {
    if(this.state.sizedFailed[url]) return;
    this.setState({ sizedFailed: { ...this.state.sizedFailed, [url]: true } });
  }

  // 'media' for a gid field, 'shop_image' for a theme reference, null for a plain URL field
  refKind = () => {
    if(isMediaImageGid(this.props.source) || isMediaImageGid(this.props.target)) return 'media';
    if(isShopImageRef(this.props.source) || isShopImageRef(this.props.target)) return 'shop_image';
    return null;
  }

  // a field that holds a reference gets a reference back, as Translate & Adapt stores it
  isRefField = () => this.refKind() !== null;

  // the value to store for a picked file: its URL, its shop image reference, or its gid - the
  // last one only when the picker delivered it (null otherwise, the caller reports)
  fieldValueFor = (url, gid = null) => {
    const kind = this.refKind();
    if(kind === 'media') {
      if(!gid) return null;
      rememberMediaImage(gid, url);
      return gid;
    }
    if(kind === 'shop_image') {
      const ref = shopImageRef(url);
      rememberShopImage(ref, url);
      return ref;
    }
    return url;
  }

  // the rows load the versioned URL, which Shopify's CDN may still answer for a deleted file:
  // the probe decides the broken state instead
  probeValues = (recheck = false) => {
    [this.props.source, this.props.target].forEach(value => {
      const url = this.displayUrl(value);
      if(!url || this.state.brokenUrls[url] === true) return;
      probeImage(url, broken => {
        if(broken === true && !this.unmounted) this.handleImageError(url);
      }, recheck);
    });
  }

  handleImageError = (url) => {
    this.setState(prev => ({ brokenUrls: { ...prev.brokenUrls, [url]: true } }));
  }

  // A comparison image in the dialog: spinner while a shop image reference resolves and while
  // the file itself loads (a bare <img> shows the browser's broken glyph in the meantime, which
  // the arrow navigation made visible on every step); broken glyph only for a dead URL
  renderCompareImage = (value, url) => {
    const name = this.filenameFromUrl(value);
    if(url === undefined) return <CircularProgress size={28} />;
    if(url === null || this.state.brokenUrls[url] === true) return <BrokenImageIcon fontSize="large" color="disabled" />;
    const loaded = imageMeta.loaded[url] === true;
    return <React.Fragment>
      {!loaded && <CircularProgress size={28} style={{ position: 'absolute' }} />}
      <img
        src={url}
        alt={name}
        title={name}
        style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: 2, visibility: loaded ? 'visible' : 'hidden' }}
        onLoad={e => {
          this.rememberDimensions(url, e.target);
          this.noteMeta('loaded', url, true);
        }}
        onError={() => this.handleImageError(url)}
      />
    </React.Fragment>
  }

  noteMeta = (kind, url, value) => {
    imageMeta[kind][url] = value;
    if(!this.unmounted) this.setState(prev => ({ metaTick: prev.metaTick + 1 }));
  }

  rememberDimensions = (url, img) => {
    if(!url || !img.naturalWidth || imageMeta.dimensions[url]) return;
    this.noteMeta('dimensions', url, { width: img.naturalWidth, height: img.naturalHeight });
    this.fetchFileSize(url);
  }

  // the byte size is known for an upload; anything else is a HEAD request that stays silent
  // where the host sends no CORS headers
  fetchFileSize = (url) => {
    if(imageMeta.fileSizes[url] !== undefined) return;
    if(url.startsWith('blob:')) {
      const bytes = this.state.selectedFile && this.state.selectedFilePreview === url ? this.state.selectedFile.size : null;
      this.noteMeta('fileSizes', url, bytes);
      return;
    }
    imageMeta.fileSizes[url] = null; // requested; stays null if the host does not answer
    fetch(url, { method: 'HEAD', mode: 'cors' })
      .then(res => {
        const bytes = Number(res.headers.get('content-length'));
        if(bytes > 0) this.noteMeta('fileSizes', url, bytes);
      })
      .catch(() => {});
  }

  // "1200 × 400 px · 3:1"; ratios that do not reduce to small integers are shown as 1.78:1
  formatDimensions = (url) => {
    const d = imageMeta.dimensions[url];
    if(!d) return '';
    const gcd = (a, b) => b ? gcd(b, a % b) : a;
    const g = gcd(d.width, d.height);
    const ratio = (d.width / g <= 20 && d.height / g <= 20)
      ? `${d.width / g}:${d.height / g}`
      : `${(d.width / d.height).toFixed(2).replace(/\.?0+$/, '')}:1`;
    return `${d.width} × ${d.height} px · ${ratio}`;
  }

  formatBytes = (bytes) => {
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), 3);
    return `${+(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${['B', 'kB', 'MB', 'GB'][i]}`;
  }

  renderDetails = (url, name) => {
    const dimensions = this.formatDimensions(url);
    if(!dimensions && !name) return null;
    const bytes = imageMeta.fileSizes[url];
    // the box's footer: a hairline divider across the whole box separates it from the image
    return <Typography variant="caption" color="text.secondary" component="div" sx={{ marginTop: '8px', paddingTop: '8px', flexShrink: 0, lineHeight: 1.5, borderTop: '1px solid', borderColor: 'divider', marginX: '-8px', paddingX: '8px' }}>
      <div style={{ fontWeight: 500 }}>{this.props.t('imageField.detailsLabel')}</div>
      {name && <div title={name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>}
      {dimensions && <div>{dimensions}{bytes ? ` · ${this.formatBytes(bytes)}` : ''}</div>}
    </Typography>
  }

  // a candidate that will not fit where the original sits: aspect ratio off by more than a
  // quarter (4:3 against 1:1 warns, 5:4 does not), or either side more than twice as long or
  // short. Small resolution changes are fine.
  dimensionsWarning = (sourceUrl, targetUrl) => {
    const a = imageMeta.dimensions[sourceUrl];
    const b = imageMeta.dimensions[targetUrl];
    if(!a || !b || sourceUrl === targetUrl) return null;
    const px = d => `${d.width} × ${d.height}`;
    const values = { source: px(a), target: px(b) };
    const ratio = (a.width / a.height) / (b.width / b.height);
    if(ratio > 1.25 || ratio < 1 / 1.25) return this.props.t('imageField.ratioWarning', values);
    const scale = Math.max(a.width / b.width, b.width / a.width, a.height / b.height, b.height / a.height);
    if(scale > 2) return this.props.t('imageField.sizeWarning', values);
    return null;
  }

  // MUI hands focus back to the opener when a dialog closes, and the browser scrolls that
  // element into view. Inside the virtualized list the opener's focusable ancestor is the
  // list container, taller than the viewport, so the list jumped on every close. The focus
  // return stays, without the scroll.
  rememberOpener = () => {
    this.openerElement = document.activeElement;
  }

  restoreOpenerFocus = () => {
    if(this.openerElement && typeof this.openerElement.focus === 'function') {
      this.openerElement.focus({ preventScroll: true });
    }
    this.openerElement = null;
  }

  handleOpenPreview = (url) => {
    if(!url) return;
    this.rememberOpener();
    this.setState({ showPreview: url });
  }

  handleClosePreview = () => {
    this.setState({ showPreview: null });
  }

  filenameFromUrl = (url) => {
    if(!url) return '';
    if(isShopImageRef(url)) return shopImageFilename(url);
    if(isMediaImageGid(url)) {
      // the resolved file's name once known, the bare id until then
      const resolved = this.state.resolvedRefs[url];
      return resolved ? this.filenameFromUrl(resolved) : url.trim().split('/').pop();
    }
    try {
      return decodeURIComponent(new URL(url).pathname.split('/').pop());
    } catch {
      return url.split('/').pop().split('?')[0];
    }
  }

  // every action in the dialog applies immediately and closes it - the row is the confirmation,
  // persistence happens with the item's own Save
  handleClearImage = () => {
    this.props.onChange(null, {
      id: this.props.itemId,
      changed: this.props.target !== '',
      initial: this.props.target,
      target: '',
    });
    this.setState({ showEditDialog: false });
  }

  // selecting a file no longer uploads right away - the option's "use this image" button does.
  // an empty list means the preview's delete button was used, so the selection resets
  handleFileChange = files => {
    if(this.state.selectedFilePreview) URL.revokeObjectURL(this.state.selectedFilePreview);
    if(typeof files[0] === 'undefined') {
      this.setState({ selectedFile: null, selectedFilePreview: null });
      return false;
    }
    this.setState({ selectedFile: files[0], selectedFilePreview: URL.createObjectURL(files[0]), uploadError: null });
  };

  handleFileUpload = () => {
    const file = this.state.selectedFile;
    const formData = new FormData();
    formData.append('image', file);

    this.setState({isPending: true});
    API.uploadMedia(formData, response => {
      if(response.success == true) {
        const value = this.fieldValueFor(response.url, response.gid || null);
        if(value === null) {
          // a gid field needs the file's gid; an older backend answers with the URL alone
          this.setState({isPending: false, uploadError: this.props.t('imageField.uploadFailed')});
          return;
        }
        this.props.onChange(null, {
          id: this.props.itemId,
          changed: true,
          initial: this.props.target,
          target: value
        });
        initialFilePage = null;
        this.setState({isPending: false, showEditDialog: false})
      } else {
        this.setState({isPending: false, uploadError: response.message || this.props.t('imageField.uploadFailed')})
      }
    });
  }

  urlIsValid = (value) => /^https?:\/\//.test(value);

  // whether a typed URL may be applied: syntax valid, and the image behind it loads.
  // 'pending' while the probe runs
  urlCheckState = (value) => {
    if(!this.urlIsValid(value)) return 'invalid';
    const loaded = this.state.urlChecks[value];
    return loaded === undefined ? 'pending' : (loaded ? 'ok' : 'missing');
  }

  handleUrlInputChange = event => {
    const value = event.target.value;
    const trimmed = value.trim();
    this.setState({ urlInput: value, uploadError: null });
    if(!this.urlIsValid(trimmed)) return;
    // a valid URL is probed as soon as it is typed; the result also drives the live preview
    if(this.state.urlChecks[trimmed] === undefined) {
      const probe = new Image();
      const done = (loaded) => {
        if(this.unmounted) return;
        this.setState(prev => ({ urlChecks: { ...prev.urlChecks, [trimmed]: loaded } }));
      };
      probe.onload = () => done(true);
      probe.onerror = () => done(false);
      probe.src = trimmed;
    }
  }

  // the same file as the source, the CDN version suffix aside: a translation to it would only add
  // a row and a storefront swap of the image for itself
  isSourceImage = (url) => {
    const strip = (u) => String(u || '').trim().split('?')[0];
    return strip(url) !== '' && strip(url) === strip(this.props.source);
  }

  handleApplyUrl = () => {
    const url = this.state.urlInput.trim();
    if(this.urlCheckState(url) !== 'ok') return;
    if(this.isSourceImage(url)) {
      this.setState({ uploadError: this.props.t('imageField.sameAsSource') });
      return;
    }

    this.props.onChange(null, {
      id: this.props.itemId,
      changed: true,
      initial: this.props.target,
      target: url,
    });
    this.setState({ urlInput: '', showEditDialog: false });
  }

  handleFileQueryChange = event => {
    this.setState({ fileQuery: event.target.value });
    // live search, debounced; an emptied field falls back to the browse list
    clearTimeout(this.fileSearchTimer);
    this.fileSearchTimer = setTimeout(() => this.handleFileSearch(false), 400);
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.fileSearchTimer);
  }

  // searches the shop's Files section by filename/URL (files() has no alt filter);
  // an empty query is browse mode: the newest files
  handleFileSearch = (loadMore = false) => {
    const query = this.state.fileQuery.trim();

    const seq = (this.fileSearchSeq = (this.fileSearchSeq || 0) + 1);
    this.setState({ fileSearchPending: true });
    API.searchMore('images', query, loadMore ? this.state.fileCursor : null, res => {
      if(seq !== this.fileSearchSeq) return; // a newer search superseded this one
      const images = res?.results?.images;
      if(res?.success !== true || !images) {
        this.setState({ fileSearchPending: false, fileResults: loadMore ? this.state.fileResults : [] });
        return;
      }
      if(query === '' && !loadMore) initialFilePage = { results: images.results, cursor: images.cursor || null };
      this.setState({
        fileSearchPending: false,
        fileResults: loadMore ? [...(this.state.fileResults || []), ...images.results] : images.results,
        fileCursor: images.cursor || null,
      });
    });
  }

  handlePickFile = (url) => {
    const file = (this.state.fileResults || []).find(f => f.title === url);
    const value = this.fieldValueFor(url, file ? file.gid : null);
    if(value === null) {
      this.setState({ uploadError: this.props.t('imageField.fileRejected') });
      return;
    }
    if(this.isSourceImage(url)) {
      this.setState({ uploadError: this.props.t('imageField.sameAsSource') });
      return;
    }
    this.props.onChange(null, {
      id: this.props.itemId,
      changed: true,
      initial: this.props.target,
      target: value,
    });
    this.setState({ showEditDialog: false });
  }

  handleOpenEditDialog = () => {
    this.rememberOpener();
    if(this.state.selectedFilePreview) URL.revokeObjectURL(this.state.selectedFilePreview);
    this.setState({showEditDialog: true, expandedOption: lastDialogView.expandedOption, selectedFile: null, selectedFilePreview: null, selectedFileUrl: null, urlInput: '', showOriginal: lastDialogView.showOriginal, uploadError: null})
    // the dialog is interactable immediately; the browse list streams in behind it
    if(this.state.fileResults === null && !this.state.fileSearchPending) {
      if(initialFilePage) {
        this.setState({ fileResults: initialFilePage.results, fileCursor: initialFilePage.cursor });
      } else {
        this.handleFileSearch(false);
      }
    }
  }

  handleCloseEditDialog = () => {
    this.setState({showEditDialog: false})
  }

  // fires in the same tick as handleFileRejected with a generic "File rejected" -
  // the functional update keeps the specific reason from being overwritten
  handleFileError = (message, variant) => {
    if(variant === 'error') {
      this.setState(prev => prev.uploadError ? null : { uploadError: message });
    }
  }

  // react-dropzone v10 rejects with bare File objects (no error codes), so the
  // reason is derived from the file itself
  handleFileRejected = (rejectedFiles) => {
    const rejection = rejectedFiles?.[0];
    const file = rejection?.file || rejection;
    const code = rejection?.errors?.[0]?.code;

    let message;
    if(code === 'file-too-large' || (file && typeof file.size === 'number' && file.size > 20000000)) {
      message = this.props.t('imageField.fileTooLarge');
      if(file && file.name) {
        message += ` (${file.name}, ${(file.size / 1000000).toFixed(1)} MB)`;
      }
    } else if(code === 'file-invalid-type' || (file && typeof file.type === 'string' && file.type !== '' && !file.type.startsWith('image/'))) {
      message = this.props.t('imageField.fileInvalidType');
      if(file && file.name) {
        message += ` (${file.name})`;
      }
    } else {
      message = this.props.t('imageField.fileRejected');
    }

    this.setState({ uploadError: message });
  }

  renderFileUpload = () => {
    return (
      <div className={classes.dropzoneWrapper}>
        <DropzoneArea
          dropzoneClass={classes.dropzone}
          dropzoneParagraphClass={classes.dropzoneText}
          dropzoneText={this.props.t('imageField.dropzoneText')}
          showPreviews={false}
          showPreviewsInDropzone={true}
          showAlerts={true}
          filesLimit={1}
          maxFileSize={20000000}
          onChange={this.handleFileChange}
          onDropRejected={this.handleFileRejected}
          onAlert={this.handleFileError}
          acceptedFiles={['image/*']}
        />
      </div>
    )
  }

  // zoomable only where no dialog covers the field (source side) - the target
  // field's dialog shows the enlarged image itself
  renderThumb = (value, zoomable = false) => {
    const url = this.displayUrl(value);
    if(url === undefined) {
      return <span className={classes.thumb} title={this.filenameFromUrl(value)}>
        <CircularProgress size={18} />
      </span>
    }
    const broken = url === null || this.state.brokenUrls[url] === true;
    const shown = broken ? null : this.sizedUrl(url, 120);
    return <span
      className={classes.thumb}
      style={broken ? {} : { backgroundImage: `url(${shown})` }}
      title={this.filenameFromUrl(value)}
      onClick={zoomable ? (e) => { e.stopPropagation(); this.handleOpenPreview(url); } : undefined}
    >
      {broken ?
        <BrokenImageIcon fontSize="small" color="disabled" />
      :
        // background images fail silently, so a hidden probe surfaces dead URLs on the thumb
        <img src={shown} alt="" style={{ display: 'none' }} onError={() => shown !== url ? this.handleSizedError(url) : this.handleImageError(url)} />
      }
    </span>
  }

  renderLargeImage = (value, zoomable = false) => {
    const url = this.displayUrl(value);
    const broken = url === null || this.state.brokenUrls[url] === true;
    return <div
      className={classes.largeImage}
      title={this.filenameFromUrl(value)}
      onClick={zoomable && url ? (e) => { e.stopPropagation(); this.handleOpenPreview(url); } : undefined}
    >
      {url === undefined ?
        <CircularProgress size={24} />
      : broken ?
        <BrokenImageIcon fontSize="large" color="disabled" />
      :
        <img src={this.sizedUrl(url, 1200)} alt={this.filenameFromUrl(value)} loading="lazy" onError={() => this.sizedUrl(url, 1200) !== url ? this.handleSizedError(url) : this.handleImageError(url)} />
      }
    </div>
  }

  // view-only zoom, closable, no further interaction
  // admin file page of the previewed image - only product images carry a MediaImage GID today
  // product images bring their media id from the sync; a theme reference gets the file's id
  // from the Files lookup that resolved it
  shopifyAdminFileUrl = () => {
    const gid = this.props.mediaGid
      || (isShopImageRef(this.props.source) ? shopImageGid(this.props.source.trim()) : null)
      || (isMediaImageGid(this.props.source) ? this.props.source.trim() : null);
    const shopUrl = this.props.shop?.shop?.url;
    if(!gid || !shopUrl || !gid.includes('gid://shopify/')) return null;
    const storeHandle = shopUrl.replace('.myshopify.com', '');
    return `https://admin.shopify.com/store/${storeHandle}/content/files/${gid.split('/').pop()}`;
  }

  renderPreviewDialog = () => {
    const { t } = this.props;
    const adminUrl = this.shopifyAdminFileUrl();
    return (
      <Dialog
        open={this.state.showPreview !== null}
        onClose={this.handleClosePreview}
        fullWidth={true}
        maxWidth={'md'}
        onClick={e => e.stopPropagation()}
        disableRestoreFocus
        TransitionProps={{ onExited: this.restoreOpenerFocus }}
      >
        <DialogTitle component="div">
          <Typography variant="h6" component="h6" noWrap style={{ paddingRight: 96 }}>{this.filenameFromUrl(this.state.showPreview || '')}</Typography>
          {adminUrl &&
            <Tooltip title={t('imageField.openInShopifyBtn')} arrow>
              <IconButton
                aria-label="Open in Shopify"
                href={adminUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ position: 'absolute', top: 8, right: 52 }}
                size="large">
                <StorefrontOutlinedIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
          }
          <IconButton
            aria-label="Close"
            onClick={this.handleClosePreview}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            size="large">
            <CloseIcon/>
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {this.state.showPreview !== null &&
            <img
              src={this.state.showPreview}
              alt={this.filenameFromUrl(this.state.showPreview)}
              className={classes.previewImage}
              style={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', width: 'auto', height: 'auto', margin: '0 auto', borderRadius: 4 }}
            />
          }
        </DialogContent>
      </Dialog>
    );
  }

  // the refusal of a chosen image, shown in the option that refused it (all three end in the
  // same "use this image" button)
  renderOptionError = (isUpload = false) => {
    if(!this.state.uploadError) return null;
    const { t } = this.props;
    return (
      <Alert severity="error" sx={{ marginTop: '8px', padding: '2px 10px', '& .MuiAlert-message': { fontSize: 14, padding: '6px 0' }, '& .MuiAlert-icon': { fontSize: 20, padding: '4px 0', marginRight: '8px' } }}>
        {isUpload && <strong>{t('imageField.uploadErrorPrefix')} </strong>}{this.state.uploadError}
      </Alert>
    );
  }

  renderFilename = (url) => {
    return <Typography variant="caption" color="text.secondary" className={classes.filename}>
      {this.filenameFromUrl(url)}
    </Typography>
  }

  renderDialog = () => {
    const { source, target, t } = this.props;
    const translated = target !== '';

    // the candidate from the currently open option previews live in the translation panel
    // before "use this image" is clicked
    let pendingPreview = null;
    if(this.state.expandedOption === 'files' && this.state.selectedFileUrl) {
      pendingPreview = this.state.selectedFileUrl;
    } else if(this.state.expandedOption === 'url' && this.urlCheckState(this.state.urlInput.trim()) === 'ok') {
      pendingPreview = this.state.urlInput.trim();
    } else if(this.state.expandedOption === 'upload' && this.state.selectedFilePreview) {
      pendingPreview = this.state.selectedFilePreview;
    }
    const shownTarget = pendingPreview || this.displayUrl(target);
    const shownTargetName = (pendingPreview && pendingPreview === this.state.selectedFilePreview && this.state.selectedFile)
      ? this.state.selectedFile.name
      : this.filenameFromUrl(pendingPreview || target);
    const dimensionsWarning = (pendingPreview || translated) ? this.dimensionsWarning(this.displayUrl(source), shownTarget) : null;

    return (
      <Dialog
        open={this.state.showEditDialog}
        onClose={this.handleCloseEditDialog}
        fullWidth={true}
        maxWidth={'lg'}
        disableRestoreFocus
        TransitionProps={{ onExited: this.restoreOpenerFocus }}
        // anchored to the top instead of centered: the content height varies (details, size
        // warning), and the arrows in the title must stay under the mouse from image to image.
        // The offset centres a dialog of its usual height, so it reads centred without moving
        sx={{
          '& .MuiDialog-container': { alignItems: 'flex-start' },
          '& .MuiDialog-paper': {
            '--dialog-top': { xs: '32px', md: `max(32px, calc((100vh - ${EDIT_DIALOG_USUAL_HEIGHT}px) / 2))` },
            marginTop: 'var(--dialog-top)',
            maxHeight: 'calc(100% - var(--dialog-top) - 32px)',
          },
        }}
        // the dialog portals out of the DOM tree but its clicks still bubble through the REACT tree
        // into the clickable field box, which would instantly re-open it
        onClick={e => e.stopPropagation()}
      >
        <LoadingIndicator
          pending={this.state.isPending}
        />

        <DialogTitle id="alert-dialog-title" component="div">
          <Typography variant="h6" component="h6">{t('imageField.dialogTitle')}</Typography>
          {/* previous/next image of the same item; buttons only, no keys - a stray keystroke must
              never carry the merchant away from the row they are working on */}
          {this.props.navigation &&
            <span style={{ position: 'absolute', top: 12, right: 60, display: 'flex', gap: 2 }}>
              <Tooltip title={t('imageField.prevImageBtn')} arrow>
                <span>
                  <IconButton aria-label={t('imageField.prevImageBtn')} disabled={!this.props.navigation.hasPrev} onClick={() => this.navigate('prev')}>
                    <ChevronLeftOutlined />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('imageField.nextImageBtn')} arrow>
                <span>
                  <IconButton aria-label={t('imageField.nextImageBtn')} disabled={!this.props.navigation.hasNext} onClick={() => this.navigate('next')}>
                    <ChevronRightOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            </span>
          }
          <IconButton
            aria-label="Close"
            onClick={this.handleCloseEditDialog}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            size="large">
            <CloseIcon/>
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {/* translated: zoom on the left, options on the right - side by side keeps the dialog short.
              the columns wrap on narrow viewports. the image is capped so it never sizes the dialog.
              stretch: the clear button is pinned to the bottom of the taller column */}
          <ChooserArea style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'stretch' }}>
          <div style={{ flex: '0 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {/* the handle stretches to the image boxes only; the ratio warning sits below this row,
                so it cannot pull the handle past the original */}
            <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'nowrap' }}>
              {/* the original is tucked away by default; the vertical edge handle slides it out to the
                  left. The handle sits flush against the box next to it - the original when shown,
                  the translation otherwise - so its right corners and that box's left corners are square */}
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disableElevation
                onClick={() => this.setState({ showOriginal: !this.state.showOriginal })}
                style={{ minWidth: 30, padding: '10px 2px', alignSelf: 'stretch', flexShrink: 0, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  {this.state.showOriginal ? <ChevronRightOutlined fontSize="small" /> : <ChevronLeftOutlined fontSize="small" />}
                  <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', fontSize: 13, letterSpacing: 1.5 }}>
                    {t('imageField.originalLabel')}
                  </span>
                </span>
              </Button>
              <Collapse orientation="horizontal" in={this.state.showOriginal} timeout={300}>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <div style={{ width: COMPARE_BOX_SIZE }}>
                    <div className={classes.compareImage} style={{ position: 'relative', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                      <div className={classes.compareStage}>
                        {this.renderCompareImage(source, this.displayUrl(source))}
                      </div>
                      {this.renderDetails(this.displayUrl(source), this.filenameFromUrl(source))}
                    </div>
                  </div>
                  {/* the same source-to-target chevron every field row has */}
                  <ChevronRightOutlined sx={{ alignSelf: 'center', flexShrink: 0, color: 'text.secondary', mx: 2 }} />
                </div>
              </Collapse>
              <div style={{ width: COMPARE_BOX_SIZE, flexShrink: 0 }}>
                <div
                  className={classes.compareImage}
                  style={{ position: 'relative', cursor: pendingPreview ? undefined : 'pointer', ...(this.state.showOriginal ? {} : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }) }}
                  // the empty placeholder and the translated image invite a click: it opens the upload
                  // option, unless one is open. A pending selection is only a preview
                  onClick={pendingPreview ? undefined : () => { if(!this.state.expandedOption) this.setState({ expandedOption: 'upload' }); }}
                >
                  <div className={classes.compareStage}>
                    {(pendingPreview || translated) ?
                      this.renderCompareImage(pendingPreview || target, shownTarget)
                    :
                      <Typography variant="body2" color="text.secondary" align="center" style={{ padding: 16 }}>
                        {t('imageField.noTranslationYet')}
                      </Typography>
                    }
                  </div>
                  {(pendingPreview || translated) && this.renderDetails(shownTarget, shownTargetName)}
                </div>
              </div>
            </div>
                {dimensionsWarning &&
                  <Alert
                    severity="warning"
                    sx={{
                      width: COMPARE_BOX_SIZE,
                      boxSizing: 'border-box',
                      marginTop: '8px',
                      padding: '2px 10px',
                      '& .MuiAlert-message': { fontSize: 13, padding: '6px 0' },
                      '& .MuiAlert-icon': { fontSize: 20, padding: '4px 0', marginRight: '8px' },
                    }}
                  >
                    {dimensionsWarning}
                  </Alert>
                }
            </div>
          <div style={{ flex: '1 1 270px', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: COMPARE_BOX_SIZE }}>
          {/* one option expanded at a time; every option ends in the same "use this image" button */}
          <Accordion variant="outlined" disableGutters style={{ marginBottom: 8, borderRadius: 4 }}
            expanded={this.state.expandedOption === 'upload'}
            onChange={(e, expanded) => this.setState({ expandedOption: expanded ? 'upload' : null, uploadError: null })}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
              <CloudUploadOutlinedIcon fontSize="small" style={{ marginRight: 10, opacity: 0.7 }} />
              <Typography variant="body2">{t('imageField.uploadOptionTitle')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {this.renderFileUpload()}
              {this.state.selectedFile &&
                <Typography variant="caption" color="text.secondary" align="center" style={{ display: 'block', marginTop: 6 }}>
                  {this.state.selectedFile.name}
                </Typography>
              }
              {this.renderOptionError(true)}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <Button color="primary" variant="contained" size="small" disabled={!this.state.selectedFile} onClick={this.handleFileUpload}>
                  {t('imageField.useImageBtn')}
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>

          <Accordion variant="outlined" disableGutters style={{ marginBottom: 8, borderRadius: 4 }}
            expanded={this.state.expandedOption === 'files'}
            onChange={(e, expanded) => this.setState({ expandedOption: expanded ? 'files' : null, uploadError: null })}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
              <PhotoLibraryOutlinedIcon fontSize="small" style={{ marginRight: 10, opacity: 0.7 }} />
              <Typography variant="body2">{t('imageField.filesOptionTitle')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                id="file_search"
                label={t('imageField.fileSearchLabel')}
                placeholder={t('imageField.fileSearchPlaceholder')}
                value={this.state.fileQuery}
                onChange={this.handleFileQueryChange}
                onKeyDown={e => { if(e.key === 'Enter') this.handleFileSearch(false); }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {this.state.fileResults !== null && this.state.fileResults.length === 0 && !this.state.fileSearchPending &&
                <Typography variant="body2" color="text.secondary" style={{ display: 'block', marginTop: 8 }}>
                  {t('imageField.noFilesFound')}
                </Typography>
              }
              <List dense style={{ maxHeight: 'min(240px, 30vh)', overflowY: 'auto', padding: 0, marginTop: 8 }}>
                {(this.state.fileResults || []).map(file => (
                  <ListItemButton
                    key={file.gid}
                    selected={this.state.selectedFileUrl === file.title}
                    onClick={() => this.setState({ selectedFileUrl: file.title, uploadError: null })}
                    style={{ borderRadius: 4 }}
                  >
                    <ListItemAvatar>
                      <img
                        src={file.title}
                        alt=""
                        loading="lazy"
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={this.filenameFromUrl(file.title)}
                      secondary={[file.alt, file.createdAt ? new Date(file.createdAt).toLocaleString() : null].filter(Boolean).join(' — ')}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true, fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
              {this.state.fileSearchPending &&
                <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                  <CircularProgress size={22} />
                </div>
              }
              {this.state.fileCursor && !this.state.fileSearchPending &&
                <Button size="small" fullWidth onClick={() => this.handleFileSearch(true)}>
                  {t('imageField.loadMoreBtn')}
                </Button>
              }
              {this.renderOptionError()}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <Button color="primary" variant="contained" size="small" disabled={!this.state.selectedFileUrl} onClick={() => this.handlePickFile(this.state.selectedFileUrl)}>
                  {t('imageField.useImageBtn')}
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* a theme setting cannot point at a foreign host, and the library already finds the shop's files by name */}
          {!this.isRefField() &&
          <Accordion variant="outlined" disableGutters style={{ marginBottom: 8, borderRadius: 4 }}
            expanded={this.state.expandedOption === 'url'}
            onChange={(e, expanded) => this.setState({ expandedOption: expanded ? 'url' : null, uploadError: null })}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
              <LinkOutlinedIcon fontSize="small" style={{ marginRight: 10, opacity: 0.7 }} />
              <Typography variant="body2">{t('imageField.urlOptionTitle')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert
                severity="warning"
                sx={{
                  marginBottom: '10px',
                  padding: '2px 10px',
                  '& .MuiAlert-message': { fontSize: 14, padding: '6px 0' },
                  '& .MuiAlert-icon': { fontSize: 20, padding: '4px 0', marginRight: '8px' },
                }}
              >
                <Trans i18nKey="imageField.externalUrlWarning" components={{ b: <strong /> }} />
              </Alert>
              <TextField
                id="image_url"
                label={t('imageField.urlInputLabel')}
                type="url"
                placeholder="https://"
                value={this.state.urlInput}
                onChange={this.handleUrlInputChange}
                onKeyDown={e => { if(e.key === 'Enter') this.handleApplyUrl(); }}
                error={this.state.urlInput.trim() !== '' && ['invalid', 'missing'].includes(this.urlCheckState(this.state.urlInput.trim()))}
                helperText={this.state.urlInput.trim() === '' ? null : {
                  invalid: t('imageField.urlInvalid'),
                  missing: t('imageField.urlUnreachable'),
                  pending: t('imageField.urlChecking'),
                }[this.urlCheckState(this.state.urlInput.trim())] || null}
                FormHelperTextProps={{ style: { fontSize: 14 } }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              {this.renderOptionError()}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <Button color="primary" variant="contained" size="small" disabled={this.urlCheckState(this.state.urlInput.trim()) !== 'ok'} onClick={this.handleApplyUrl}>
                  {t('imageField.useImageBtn')}
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
          }

          {translated &&
            <div style={{ marginTop: 'auto' }}>
              {/* pinned to the bottom of the column, clearly set apart - clearing should never be a stray click */}
              <Divider style={{ margin: '20px 0 16px 0' }} />
              <Button color="error" variant="outlined" fullWidth onClick={this.handleClearImage}>
                <DeleteIcon fontSize="small" style={{marginRight: 6}} /> {t('imageField.clearTranslationBtn')}
              </Button>
            </div>
          }
          </div>
          </ChooserArea>
        </DialogContent>
      </Dialog>
    );
  }

  render() {
    const { variant, source, target, label, rows, t } = this.props;
    const translated = target !== '';

    // next to multiline text fields (the theme section) the row is capped at their rendered
    // 70px - the 5-row textareas do not reach their nominal height, the input's height override
    // collapses them, so the value is taken from the screen. In the image groups of products,
    // collections and articles the row keeps its roomier 90px
    const compact = Boolean(rows);
    // the large view (toggle in the images group header): image stage on top, name and button below
    const large = this.props.large === true && !compact;
    const boxStyle = large
      ? { padding: 12, paddingTop: 16, flexDirection: 'column', alignItems: 'stretch', gap: 8 }
      : compact
        ? { minHeight: 70, padding: '6px 16px', '--ly-image-thumb': '46px' }
        : { minHeight: 90, padding: 20, '--ly-image-thumb': '50px' };

    if(large && variant === 'source') {
      return (
        <Root className={classNames(classes.box, 'ly-image-field-source', classes.boxClickable)} style={boxStyle}>
          <span className={classes.labelChip}>{label ? label : t('imageField.defaultLabel')}</span>
          {this.renderLargeImage(source, true)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 40 }}>
            {this.renderFilename(source)}
          </div>
          {this.renderPreviewDialog()}
        </Root>
      );
    }

    if(large) {
      return (
        <Root className={classNames(classes.box, 'ly-image-field-target', classes.boxClickable, this.props.broken && classes.broken, this.props.highlighted && classes.highlighted)} style={boxStyle} onClick={this.handleOpenEditDialog}>
          {translated ?
            this.renderLargeImage(target)
          :
            <div className={classes.largeImage}>
              <Typography variant="body2" color="text.secondary">{t('imageField.noTranslationYet')}</Typography>
            </div>
          }
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 40 }}>
            {translated && this.renderFilename(target)}
            <Tooltip title={t('imageField.changeBtn')} arrow>
              <IconButton className={classNames(classes.editBtn, classes.changeBtn)} onClick={this.handleOpenEditDialog} size="medium">
                {translated ? <CreateIcon /> : <AddIcon />}
              </IconButton>
            </Tooltip>
          </div>
          {this.renderDialog()}
        </Root>
      );
    }

    if(variant === 'source') {
      return (
        // like the target side, the whole field is the click target - it opens the zoom preview
        <Root
          className={classNames(classes.box, 'ly-image-field-source', classes.boxClickable)}
          style={boxStyle}
          onClick={() => this.handleOpenPreview(this.displayUrl(source))}
        >
          <span className={classes.labelChip}>{label ? label : t('imageField.defaultLabel')}</span>
          {this.renderThumb(source, true)}
          {this.renderFilename(source)}
          {this.renderPreviewDialog()}
        </Root>
      );
    }

    return (
      // the whole target field is the click target in both states, the icon is just the affordance
      <Root
        className={classNames(classes.box, 'ly-image-field-target', classes.boxClickable, this.props.broken && classes.broken, this.props.highlighted && classes.highlighted)}
        style={boxStyle}
        onClick={this.handleOpenEditDialog}
      >
        {translated &&
          <React.Fragment>
            {this.renderThumb(target)}
            {this.renderFilename(target)}
          </React.Fragment>
        }
        <Tooltip title={t('imageField.changeBtn')} arrow>
          <IconButton
            className={classNames(classes.editBtn, classes.changeBtn)}
            onClick={this.handleOpenEditDialog}
            size={compact ? 'small' : 'medium'}
          >
            {translated ? <CreateIcon /> : <AddIcon />}
          </IconButton>
        </Tooltip>
        {this.renderDialog()}
      </Root>
    );
  }
}

const mapStateToProps = ({ shop, themes }) => ({
  shop: shop,
  themes: themes.themes,
});

export default withTranslation()(withRouter(connect(
  mapStateToProps,
  null
)(ImageField)));
